<template>
  <v-dialog v-model="dialog" max-width="800px">
    <template v-slot:activator="{ on }">
      <v-list-item v-on="on">
        <v-list-item-title>Exportar</v-list-item-title>
      </v-list-item>
    </template>
    <v-card height="560px" class="card-content">
      <v-card-title>
        <span class="segment-title">Exportar segmentação</span>
      </v-card-title>
      <div class="content-grow px-2">
        <div class="segment-info-container px-4">
          <span class="segment-name">
            {{ segment && segment.name }} -
            <v-progress-circular v-if="updating" indeterminate color="primary" size="15" />
            <span v-else>
              {{ segment && segment.clients_qty | localeString }}
            </span>
          </span>
          <p class="segment-description">{{ segment && segment.description }}</p>

          <div v-if="pageStep === 2" class="segment-model mt-4">
            <span>Modelo: </span>
            <p>Lista de clientes</p>
          </div>
        </div>
        <div v-if="hasPermissionToExportPii">
          <v-card-text v-if="pageStep === 1" class="pt-0">
            <v-radio-group v-model="exportType">
              <v-radio v-if="hasPermissionToExportPii" color="primary" label="Lista de clientes" value="clientes" />
              <v-container v-if="hasPermissionToExportPii && exportType === 'clientes'" class="mb-3 pa-0" fluid grid-list-lg>
                <v-layout>
                  <v-flex v-for="(dataType, index) in dataTypes" :key="index" class="px-3">
                    <v-checkbox v-model="dataType.selected" color="primary" :label="dataType.checkboxLabel" :disabled="dataType.disabled" />
                    <p v-for="(property, index) in dataType.properties" :key="index" class="mf-body-2">
                      <mf-icon small>chevron_right</mf-icon>
                      {{ property.friendly }}
                    </p>
                  </v-flex>
                </v-layout>
              </v-container>
              <v-radio color="primary" label="Produtos consumidos" value="produtosConsumidos" />
              <v-radio color="primary" label="Informações de compra" value="purchaseInfo" />
              <v-radio color="primary" label="Informações de compra e proximidade por loja" value="purchasePerUnit" />
            </v-radio-group>
            <v-container v-if="exportsWithDate.includes(exportType) || exportsWithUnits.includes(exportType)" class="pa-0" fluid grid-list-lg>
              <v-layout>
                <v-flex v-if="exportsWithDate.includes(exportType)">
                  <mf-date-range-picker label="Data inicial - Data final" :initial-date.sync="dataInicial" :final-date.sync="dataFinal" />
                </v-flex>
                <v-flex v-if="exportsWithUnits.includes(exportType)">
                  <mf-select-units-dialog
                    hide-details
                    required
                    outlined
                    label="Lojas"
                    units-label=""
                    check-all-units-on-init
                    enable-inactive-units-selection
                    :selected-units.sync="unitsIds"
                    :selected-units-configuration.sync="units"
                    :units="allUnits"
                    :tag-list="tagList"
                    :group-list="unitsGroups"
                  />
                </v-flex>
              </v-layout>
            </v-container>
          </v-card-text>
          <include-emails-table v-else ref="includeEmailsTable" :emails.sync="emails" scroll-height="200px" />
        </div>
        <v-card-text v-else>
          <mf-info-card
            type="info"
            title="Sem permissão para exportar dados pessoais sensíveis"
            class="info-card"
            description="Você não tem permissão para exportar dados pessoais sensíveis, verifique suas permissões com seu administrador."
          />
        </v-card-text>
      </div>
      <v-card-actions class="d-flex justify-end mr-2">
        <mf-button
          v-if="pageStep === 2"
          outlined
          :disabled="exportButtonDisable"
          :loading="loading || $wait.is(['export-segment-clients', 'export-segment-products']) || updating"
          class="mr-3"
          label="Voltar"
          @click="() => (pageStep = 1)"
        />
        <mf-button
          v-if="['clientes', 'purchaseInfo', 'purchasePerUnit'].includes(exportType) && pageStep === 1"
          :disabled="exportButtonDisable"
          :loading="loading || $wait.is(['export-segment-clients', 'export-segment-products']) || updating"
          label="Exportar"
          @click="exportar"
        />
        <export-data
          v-else
          async
          :disabled="exportButtonDisable"
          :loading="loading || $wait.is(['export-segment-clients', 'export-segment-products']) || updating"
          :disableXLSX="!['clientes', 'purchaseInfo'].includes(exportType)"
          @file-format="getFileFormat"
        ></export-data>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import flatten from 'lodash/flatten'
import cloneDeep from 'lodash/cloneDeep'
import { mapGetters } from 'vuex'
import { mapWaitingActions } from 'vue-wait'
import { DateTime } from 'luxon'
import { SEND_SEGMENTATION_REPORT, MUTATION_SEND_PURCHASE_INFO, MUTATION_SEND_PURCHASE_PER_UNIT } from '../../graphql'
import ExportData from '../../../../../components/export-data/ExportData.vue'
import IncludeEmailsTable from '@/components/export-email-report/IncludeEmailsTable.vue'

const defaultDataTypes = [
  {
    id: 1,
    selected: true,
    disabled: true,
    checkboxLabel: 'Dados básicos',
    properties: [
      { csv: 'CPF_CNPJ', friendly: 'CPF ou CNPJ' },
      { csv: 'CODIGO', friendly: 'ID do cliente' },
      { csv: 'NOME', friendly: 'Nome' },
    ],
  },
  {
    id: 2,
    selected: false,
    checkboxLabel: 'Dados cadastrais',
    properties: [{ csv: 'DATA_CADASTRO', friendly: 'Data do cadastro' }],
  },
  {
    id: 3,
    selected: false,
    checkboxLabel: 'Dados de contato',
    properties: [
      { csv: 'FONE_1', friendly: 'Telefone 1' },
      { csv: 'FONE_2', friendly: 'Telefone 2' },
      { csv: 'EMAIL', friendly: 'E-mail' },
      { csv: 'END_RUA,END_NUMERO,END_BAIRRO,END_CIDADE,END_ESTADO,END_CEP,END_PAIS,END_LATITUDE,END_LONGITUDE', friendly: 'Endereço' },
    ],
  },
  {
    id: 4,
    selected: false,
    checkboxLabel: 'Dados demográficos',
    properties: [
      { csv: 'DATA_NASCIMENTO', friendly: 'Data de nascimento' },
      { csv: 'GENERO', friendly: 'Gênero' },
      { csv: 'TIPO_PESSOA', friendly: 'Tipo de pessoa' },
      { csv: 'CLIENTE_DO_CLUBE', friendly: 'Tipo de cliente' },
    ],
  },
]

export default {
  components: { IncludeEmailsTable, ExportData },
  props: { segment: Object },
  data: () => ({
    segmentoSelecionado: null,
    dataInicial: '',
    dataFinal: '',
    exportType: 'clientes',
    dialog: false,
    updating: false,
    pageStep: 1,
    emails: [],
    loading: false,
    dataTypes: [],
    fileFormat: 'csv',
    unitsIds: [],
    units: [],
  }),
  computed: {
    ...mapGetters(['tagList', 'unitsGroups']),
    hasPermissionToExportPii() {
      return this.hasPiiAccess && this.hasPiiDownloadAccess
    },
    allUnits() {
      return this.$store.getters.units
    },
    exportsWithDate() {
      return ['produtosConsumidos', 'purchaseInfo', 'purchasePerUnit']
    },
    exportsWithUnits() {
      return ['produtosConsumidos', 'purchaseInfo', 'purchasePerUnit']
    },
    refreshedSegments() {
      return this.$store.getters.refreshedSegments
    },
    segmentsUpdating() {
      return this.$store.getters.updatingSegmentsIds
    },
    exportButtonDisable() {
      if (!this.hasPermissionToExportPii) return true
      if (this.exportsWithDate.includes(this.exportType)) return !this.dataInicial || !this.dataFinal
      return false
    },
    asyncDataTypes() {
      let dataTypes = []
      const types = { 1: 'BASIC_DATA', 2: 'REGISTRATION_DATA', 3: 'CONTACT_DATA', 4: 'DEMOGRAPHIC_DATA' }
      const filteredDataTypes = this.dataTypes.filter((d) => d.selected).map((d) => d.id)
      for (let dataType of filteredDataTypes) dataTypes.push(types[dataType])
      return dataTypes
    },
    hasPiiAccess() {
      return this.$store.getters.hasPiiAccess
    },
  },
  watch: {
    dialog(value) {
      if (!value) return
      this.dataTypes = cloneDeep(defaultDataTypes)
      this.$emit('modal-open')
    },
    segmentsUpdating(value) {
      this.updating = value.includes(this.segment._id)
    },
    refreshedSegments(refreshedSegments) {
      const index = refreshedSegments.findIndex((r) => r._id === this.segment._id)
      if (index > -1) {
        this.segment.clients_refreshed_at = refreshedSegments[index].clients_refreshed_at
        this.segment.clients_qty = refreshedSegments[index].clients_qty
      }
      this.updating = false
    },
  },
  async mounted() {
    if (!this.hasPermissionToExportPii) {
      this.exportType = 'produtosConsumidos'
    }
  },
  methods: {
    ...mapWaitingActions({
      exportSegmentClients: 'export-segment-clients',
      exportSegmentProducts: 'export-segment-products',
    }),
    getFileFormat(value) {
      this.fileFormat = value
      this.exportar()
    },
    exportarPadraoConsumo(now) {
      this.exportSegmentProducts({
        filter: { id_segment: this.segment._id, date: [this.dataInicial, this.dataFinal], file_format: this.fileFormat, units: this.units },
        fileName: `lista-de-produtos-consumidos-segmentacao-${now}`,
      })
    },

    async exportarPurchaseInfo() {
      if (this.pageStep === 1) {
        this.pageStep = 2
      } else {
        const isValid = this.$refs.includeEmailsTable.isValid()
        if (!isValid) return

        this.loading = true
        await this.$apollo
          .mutate({
            mutation: MUTATION_SEND_PURCHASE_INFO,
            variables: {
              emails: this.emails,
              unitsConfiguration: this.units,
              endDate: this.dataFinal,
              startDate: this.dataInicial,
              segmentationId: this.segment._id,
              fileFormat: this.fileFormat.toUpperCase(),
            },
            context: {
              uri: this.$microservicesUrls['asyncJobs'],
            },
          })
          .then(() => {
            this.loading = false
            this.pageStep = 1
            this.clearFieldsAndClose()
            this.$snackbar({ message: 'E-mail enviado com sucesso', snackbarColor: 'success' })
          })
          .catch(() => {
            this.loading = false
            this.$snackbar({ message: 'Erro ao exportar relatório', snackbarColor: 'error' })
          })
      }
    },

    closeModal() {
      this.$refs.includeEmailsTable.errorEmail = ''
      this.dialog = false
    },
    clearFieldsAndClose() {
      this.emails = cloneDeep(this.$refs.includeEmailsTable.defaultEmails)
      this.closeModal()
    },

    async exportClientes(now) {
      if (this.pageStep === 1) {
        this.pageStep = 2
      } else {
        const isValid = this.$refs.includeEmailsTable.isValid()
        if (!isValid) return

        this.loading = true
        await this.$apollo
          .mutate({
            mutation: SEND_SEGMENTATION_REPORT,
            variables: {
              emails: this.emails,
              groups: this.asyncDataTypes,
              segmentationId: this.segment._id,
              fileFormat: this.fileFormat.toUpperCase(),
            },
            context: {
              uri: this.$microservicesUrls['asyncJobs'],
            },
          })
          .then(() => {
            this.loading = false
            this.pageStep = 1
            this.clearFieldsAndClose()
            this.$snackbar({ message: 'E-mail enviado com sucesso', snackbarColor: 'success' })
          })
          .catch(() => {
            this.loading = false
            this.$snackbar({ message: 'Erro ao exportar relatório', snackbarColor: 'error' })
          })
      }
    },

    async exportarPurchasePerUnit() {
      if (this.pageStep === 1) {
        this.pageStep = 2
        return
      }
      const isValid = this.$refs.includeEmailsTable.isValid()
      if (!isValid) return

      this.loading = true
      await this.$apollo
        .mutate({
          mutation: MUTATION_SEND_PURCHASE_PER_UNIT,
          variables: {
            emails: this.emails,
            unitsConfiguration: this.units,
            segmentationId: this.segment._id,
            endDate: this.dataFinal,
            startDate: this.dataInicial,
            fileFormat: this.fileFormat.toUpperCase(),
          },
          context: {
            uri: this.$microservicesUrls['asyncJobs'],
          },
        })
        .then(() => {
          this.loading = false
          this.pageStep = 1
          this.clearFieldsAndClose()
          this.$snackbar({ message: 'E-mail enviado com sucesso', snackbarColor: 'success' })
        })
        .catch(() => {
          this.loading = false
          this.$snackbar({ message: 'Erro ao exportar relatório', snackbarColor: 'error' })
        })
    },

    exportar() {
      const now = DateTime.now().toFormat('dd-MM-yy')

      if (this.exportType === 'produtosConsumidos') this.exportarPadraoConsumo(now)
      if (this.exportType === 'purchaseInfo') this.exportarPurchaseInfo()
      if (this.exportType === 'clientes') this.exportClientes(now)
      if (this.exportType === 'purchasePerUnit') this.exportarPurchasePerUnit()
    },

    limparDataFinal() {
      this.dataFinal = ''
    },

    selectedFields() {
      const fields = []
      for (let dataType of this.dataTypes) {
        if (dataType.selected) {
          fields.push(dataType.properties.map((property) => property.csv))
        }
      }
      return flatten(fields)
    },
  },
}
</script>

<style lang="scss" scoped>
.card-content {
  display: flex;
  flex-direction: column;

  & .content-grow {
    height: 420px;
  }

  & .segment-title {
    color: $cinza-d10;
    font-weight: 700;
    font-size: 22px;
  }
}

.segment-info-container {
  & .segment-name {
    font-size: 16px;
    color: $cinza-d10;
  }

  & .segment-description {
    font-size: 14px;
    color: $cinza-0;
  }

  & .segment-model {
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    & span {
      color: $marca-0;
    }

    & p {
      margin: 0;
      color: $cinza-0;
    }
  }
}
</style>
